import { useEffect, useMemo, useState } from "react";

import { useReactiveVar } from "@apollo/client";
import { useTheme } from "@emotion/react";
import { LoadingPage } from "@hl/base-components/lib/LoadingPage";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import { useAuth } from "@hl/shared-features/lib/features/auth/AuthContext";
import {
  FEATURE_FLAGS,
  useFeatureFlags,
} from "@hl/shared-features/lib/features/auth/hooks";
import { identityVar } from "@hl/shared-features/lib/features/auth/vars";
import { AppSEO, NotFound } from "@hl/shared-features/lib/features/layout";
import {
  getProfileDefaultDisplayName,
  getProfileDisplayName,
} from "@hl/shared-features/lib/utils/profile";
import {
  AppShell,
  createStyles,
  Flex,
  Stack,
  Tabs,
  Title,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useNavigate, useParams } from "react-router-dom";
import { isAddress } from "viem";
import { useEnsAddress } from "wagmi";

import { USER_BASE_URL, USER_URL } from "~config";

import { AppHeader } from "../../layout/Header";
import { usePublicUserProfileQuery } from "../queries/profile.graphql.generated";

import { GeneralSettings } from "./GeneralSettings";
import { WalletSettings } from "./WalletSettings";
import { NotificationSettings } from "./notifications/NotificationSettings";

export enum AccountSettingTabs {
  General = "general",
  Notifications = "notifications",
  Wallets = "wallets",
}

const useStyles = createStyles((theme) => ({
  link: {
    textDecoration: "none",
  },
  appShellMain: {
    paddingLeft: "20px !important",
    paddingRight: "20px !important",
    [theme.fn.largerThan(theme.breakpoints.md)]: {
      paddingLeft: "40px !important",
      paddingRight: "40px !important",
    },
  },
}));

export const AccountSettings = () => {
  const { walletAddress } = useAuth();
  const { slug: originalSlug, view } = useParams<{
    slug?: string;
    view?: AccountSettingTabs;
  }>();
  const { classes } = useStyles();

  const enabledNotificationWallets = useFeatureFlags(
    FEATURE_FLAGS.ENABLED_NOTIFICATION_WALLETS
  ) as unknown as string;

  const showNotificationSettings = useMemo(() => {
    if (enabledNotificationWallets == null) return false;
    if (enabledNotificationWallets.trim() == "") return false;
    if (enabledNotificationWallets.trim() == "all") return true;

    const wallets = enabledNotificationWallets
      .trim()
      .split(",")
      .map((w) => w.trim().toLowerCase());

    if (walletAddress && wallets.includes(walletAddress.toLowerCase()))
      return true;
    return false;
  }, [enabledNotificationWallets, walletAddress]);

  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm - 1}px)`);
  const identity = useReactiveVar(identityVar);
  const slug = originalSlug?.[0] === "@" ? originalSlug.substring(1) : null;

  const [activeTab, setActiveTab] = useState<AccountSettingTabs | null>(
    AccountSettingTabs.General
  );

  const { data: slugEnsData, isLoading: loadingSlugEns } = useEnsAddress({
    chainId: 1,
    name: slug != null && !isAddress(slug) ? slug : undefined,
  });

  const { data: authEnsData, isLoading: loadingAuthEns } = useEnsAddress({
    chainId: 1,
    name:
      walletAddress != null && !isAddress(walletAddress)
        ? walletAddress
        : undefined,
  });

  const resolvedSlugAddress = useMemo(() => {
    if (slug == null) return null;
    if (isAddress(slug)) return slug;
    return slugEnsData;
  }, [slug, slugEnsData]);

  const resolvedWalletAddress = useMemo(() => {
    if (walletAddress == null) return null;
    if (isAddress(walletAddress)) return walletAddress;
    return authEnsData;
  }, [walletAddress, authEnsData]);

  const { data, loading } = usePublicUserProfileQuery({
    variables: {
      slug: resolvedSlugAddress!,
    },
    skip: resolvedSlugAddress == null,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const profile = data?.getPublicAccountSettings;
  const defaultName = getProfileDefaultDisplayName(
    profile?.walletAddresses,
    slug ?? undefined
  );

  const isOwner = !!(
    resolvedWalletAddress &&
    profile?.walletAddresses?.some(
      (address) =>
        address.toLowerCase() === resolvedWalletAddress.toLowerCase() ||
        identity?.account?.ensName === address
    )
  );

  useEffect(() => {
    if (view && Object.values(AccountSettingTabs).includes(view)) {
      if (loading) {
        return;
      }

      if (isOwner) {
        return setActiveTab(view);
      }

      return setActiveTab(AccountSettingTabs.General);
    }
  }, [view, loading, isOwner]);

  if (!slug || (!data && !loading && resolvedSlugAddress != null)) {
    return <NotFound />;
  }

  if (resolvedWalletAddress && profile?.walletAddresses && !isOwner) {
    navigate(`${USER_URL.user.profile.replace(":slug", slug ?? "")}`);
  }

  const name = getProfileDisplayName(
    profile?.displayName,
    profile?.walletAddresses,
    slug
  );

  const changeTab = (tab: string) =>
    navigate(
      `${USER_URL.user.accountSettings.default.replace(
        ":slug",
        slug ?? ""
      )}/${tab}`
    );

  if (loading || loadingSlugEns || loadingAuthEns || !profile || !isOwner) {
    return <LoadingPage />;
  }

  return (
    <>
      <AppSEO title={`${name} - Profile`} description={profile?.bio || ""}>
        <link rel="canonical" href={`${USER_BASE_URL}/@${name}`} />
      </AppSEO>

      <AppShell
        header={<AppHeader />}
        classNames={{ main: classes.appShellMain }}
      >
        <Stack spacing={24} pt={40} pb={40}>
          <Title>Account Settings</Title>
        </Stack>
        <Stack spacing={10}>
          <Tabs
            value={activeTab}
            onTabChange={changeTab}
            keepMounted={true}
            styles={{
              tab: {
                fontWeight: WEIGHT_BOLD,
              },
              tabsList: {
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap-reverse",
              },
              tabLabel: {
                display: "flex",
                alignItems: "center",
                whiteSpace: "pre",
                height: 20,
              },
            }}
          >
            <Tabs.List mb={isMobile ? 0 : 24}>
              <Flex align="center" columnGap={32} wrap="wrap">
                <Tabs.Tab value={AccountSettingTabs.General} py={13}>
                  General
                </Tabs.Tab>
                <Tabs.Tab
                  value={AccountSettingTabs.Notifications}
                  py={13}
                  hidden={!showNotificationSettings}
                >
                  Notifications
                </Tabs.Tab>
                <Tabs.Tab value={AccountSettingTabs.Wallets} py={13}>
                  Wallets
                </Tabs.Tab>
              </Flex>
            </Tabs.List>

            <Tabs.Panel value={AccountSettingTabs.General} mt={16}>
              <GeneralSettings
                defaultName={defaultName}
                profile={profile}
                active={activeTab === AccountSettingTabs.General}
              />
            </Tabs.Panel>
            <Tabs.Panel
              value={AccountSettingTabs.Notifications}
              mt={16}
              hidden={!showNotificationSettings}
            >
              <NotificationSettings
                active={activeTab === AccountSettingTabs.Notifications}
              />
            </Tabs.Panel>
            <Tabs.Panel value={AccountSettingTabs.Wallets} mt={16}>
              <WalletSettings
                active={activeTab === AccountSettingTabs.Wallets}
                walletAddresses={profile?.walletAddresses}
              />
            </Tabs.Panel>
          </Tabs>
        </Stack>
      </AppShell>
    </>
  );
};
